import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { Calendar, FileText, Upload, Camera, FileCheck, Sparkles, Scan, X, Check, RefreshCw, Smartphone, Video, Search, MessageSquare } from 'lucide-react';
import { scanReceiptOcr, CARD_COMPANIES } from '../utils/receiptOcr';

const LeaveExpenseTab = ({ mode = 'leave' }) => {
  const { currentUser, users, approvals, applyForLeave, applyForExpense, getUserPolicyGroup, withdrawLeaveRequest, withdrawExpenseRequest, requestLeaveCancellation, getAutoApprovalLine } = useContext(AppContext);
  const receiptFileInputRef = useRef(null);
  const proofFileInputRef = useRef(null);
  const leaveFormRef = useRef(null);
  const expenseFormRef = useRef(null);

  // Find current user's DB details for accurate leave counts
  const userDbDetails = users.find(u => u.UserID === currentUser.UserID) || currentUser;
  const userPolicyGroup = getUserPolicyGroup ? getUserPolicyGroup(currentUser.UserID) : null;

  const todayStr = new Date().toISOString().split('T')[0];

  // Leave Form State
  const [leaveType, setLeaveType] = useState('FULL'); // 'FULL' | 'HALF_AM' | 'HALF_PM' | 'HALF' | 'QUARTER'
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [leaveReason, setLeaveReason] = useState('');
  const [sendKakaoLeave, setSendKakaoLeave] = useState(true); // Default ON
  
  // Special Leave State
  const [isSpecial, setIsSpecial] = useState(false);
  const [specialType, setSpecialType] = useState('조부상'); // '조부상' | '부모님상' | '포상휴가' | '기타'
  const [customSpecialName, setCustomSpecialName] = useState('');

  // Proof Document State
  const [proofFile, setProofFile] = useState(null);
  const [proofFileName, setProofFileName] = useState('');
  const [proofPreviewUrl, setProofPreviewUrl] = useState('');
  const [proofUploadProgress, setProofUploadProgress] = useState(0);
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);
  const [selectedLeaveDetail, setSelectedLeaveDetail] = useState(null);
  const [cancelModalItem, setCancelModalItem] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState('');

  // Reset selected leaveType if it violates active policy constraints
  useEffect(() => {
    if (!isSpecial && userPolicyGroup) {
      const allowQuarter = (userPolicyGroup?.settings?.unit === '0.25') && userPolicyGroup?.settings?.allowPartial;
      const allowHalf = (userPolicyGroup?.settings?.unit === '0.25' || userPolicyGroup?.settings?.unit === '0.5') && userPolicyGroup?.settings?.allowPartial;
      
      if (leaveType === 'QUARTER' && !allowQuarter) {
        setLeaveType('FULL');
      } else if ((leaveType === 'HALF' || leaveType === 'HALF_AM' || leaveType === 'HALF_PM') && !allowHalf) {
        setLeaveType('FULL');
      }
    }
  }, [userPolicyGroup, leaveType, isSpecial]);

  // Expense Form State
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseFileName, setExpenseFileName] = useState('');
  const [expenseDetails, setExpenseDetails] = useState('');
  const [expenseDate, setExpenseDate] = useState(todayStr);
  const [cardCompany, setCardCompany] = useState('국민카드');
  const [customCardCompany, setCustomCardCompany] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('식대'); // '교통비' | '식대' | '소모품' | '회의비' | '기타'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isSubmittingExpense, setIsSubmittingExpense] = useState(false);
  const [selectedExpenseDetail, setSelectedExpenseDetail] = useState(null);
  const [sendKakaoExpense, setSendKakaoExpense] = useState(true); // Default ON
  
  // OCR & Camera State
  const [isOcrScanning, setIsOcrScanning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrNotice, setOcrNotice] = useState(null);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  
  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mobileCameraInputRef = useRef(null);

  // Leave Tabs local filter
  const [leaveFilter, setLeaveFilter] = useState('ALL'); // 'ALL' | 'FULL' | 'HALF' | 'SPECIAL'

  // Calendar Navigation State
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());

  // Filter approvals for current user
  const myApprovals = (approvals || []).filter(a => a && a.ApplicantID === currentUser?.UserID);
  
  // Leave approvals
  const leaveApprovals = myApprovals.filter(a => a.Type === 'LEAVE');
  
  // Expense approvals
  const expenseApprovals = myApprovals.filter(a => a.Type === 'EXPENSE');

  // Leave Period & Search Filter State
  const [leavePeriodFilter, setLeavePeriodFilter] = useState('ALL'); // 'THIS_MONTH' | '3_MONTHS' | '6_MONTHS' | 'ALL' | 'CUSTOM'
  const [leaveCustomStartDate, setLeaveCustomStartDate] = useState('');
  const [leaveCustomEndDate, setLeaveCustomEndDate] = useState('');
  const [leaveSearchTerm, setLeaveSearchTerm] = useState('');
  const [leavePage, setLeavePage] = useState(1);

  // Expense Period & Search Filter State (Requirement 6)
  const [expensePeriodFilter, setExpensePeriodFilter] = useState('THIS_MONTH'); // 'THIS_MONTH' | '3_MONTHS' | '6_MONTHS' | 'ALL' | 'CUSTOM'
  const [expenseCustomStartDate, setExpenseCustomStartDate] = useState('');
  const [expenseCustomEndDate, setExpenseCustomEndDate] = useState('');
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [expensePage, setExpensePage] = useState(1);

  const ITEMS_PER_PAGE = 20;

  // Filtered Expense Approvals List
  const filteredExpenseApprovals = useMemo(() => {
    const now = new Date();
    return expenseApprovals.filter(a => {
      const d = new Date(a.CreatedAt);
      
      // Period filter
      if (expensePeriodFilter === 'THIS_MONTH') {
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      } else if (expensePeriodFilter === '3_MONTHS') {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        if (d < threeMonthsAgo) return false;
      } else if (expensePeriodFilter === '6_MONTHS') {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        if (d < sixMonthsAgo) return false;
      } else if (expensePeriodFilter === 'CUSTOM') {
        if (expenseCustomStartDate && d < new Date(expenseCustomStartDate + 'T00:00:00')) return false;
        if (expenseCustomEndDate && d > new Date(expenseCustomEndDate + 'T23:59:59')) return false;
      }

      // Search term filter
      if (expenseSearchTerm.trim()) {
        const term = expenseSearchTerm.toLowerCase();
        const detailsMatch = (a.Details || '').toLowerCase().includes(term);
        const cardMatch = (a.CardCompany || '').toLowerCase().includes(term);
        const amountMatch = (a.Amount || '').toString().includes(term);
        const idMatch = (a.ApprovalID || '').toLowerCase().includes(term);
        if (!detailsMatch && !cardMatch && !amountMatch && !idMatch) return false;
      }

      return true;
    }).sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
  }, [expenseApprovals, expensePeriodFilter, expenseCustomStartDate, expenseCustomEndDate, expenseSearchTerm]);

  const filteredExpenseTotalAmount = filteredExpenseApprovals.reduce((sum, a) => sum + (a.Amount || 0), 0);

  // Filter leave history list with Period & Search
  const filteredLeaveApprovals = useMemo(() => {
    const now = new Date();
    return leaveApprovals.filter(a => {
      // 1. Leave Type Filter
      if (leaveFilter === 'FULL' && (a.LeaveType !== 'FULL' || a.IsSpecial)) return false;
      if (leaveFilter === 'HALF' && a.LeaveType !== 'HALF' && a.LeaveType !== 'HALF_AM' && a.LeaveType !== 'HALF_PM' && a.LeaveType !== 'QUARTER') return false;
      if (leaveFilter === 'SPECIAL' && !a.IsSpecial) return false;

      // 2. Period filter
      const d = new Date(a.CreatedAt || a.StartDate);
      if (leavePeriodFilter === 'THIS_MONTH') {
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      } else if (leavePeriodFilter === '3_MONTHS') {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        if (d < threeMonthsAgo) return false;
      } else if (leavePeriodFilter === '6_MONTHS') {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        if (d < sixMonthsAgo) return false;
      } else if (leavePeriodFilter === 'CUSTOM') {
        if (leaveCustomStartDate && d < new Date(leaveCustomStartDate + 'T00:00:00')) return false;
        if (leaveCustomEndDate && d > new Date(leaveCustomEndDate + 'T23:59:59')) return false;
      }

      // 3. Search term filter
      if (leaveSearchTerm.trim()) {
        const term = leaveSearchTerm.toLowerCase();
        const detailsMatch = (a.Details || '').toLowerCase().includes(term);
        const reasonMatch = (a.RejectReason || '').toLowerCase().includes(term);
        const dateMatch = (a.StartDate || '').includes(term) || (a.EndDate || '').includes(term);
        if (!detailsMatch && !reasonMatch && !dateMatch) return false;
      }

      return true;
    }).sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
  }, [leaveApprovals, leaveFilter, leavePeriodFilter, leaveCustomStartDate, leaveCustomEndDate, leaveSearchTerm]);

  // Expense counts & metrics
  const thisMonthExpense = expenseApprovals.filter(a => {
    const d = new Date(a.CreatedAt);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const thisMonthExpenseAmount = thisMonthExpense.reduce((sum, a) => sum + (a.Amount || 0), 0);
  const approvedExpenseAmount = expenseApprovals.filter(a => a.Status === 'APPROVED').reduce((sum, a) => sum + (a.Amount || 0), 0);
  const pendingExpenseAmount = expenseApprovals.filter(a => a.Status === 'PENDING').reduce((sum, a) => sum + (a.Amount || 0), 0);

  // Date range calculators
  const getWorkingDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    if (s > e) return 0;
    
    let count = 0;
    let cur = new Date(s);
    while (cur <= e) {
      const day = cur.getDay();
      if (day !== 0 && day !== 6) { // Exclude Sunday (0) and Saturday (6)
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  const getCalendarDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    if (s > e) return 0;
    const diffTime = Math.abs(e - s);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const isQuarter = leaveType === 'QUARTER';
  const isHalf = leaveType === 'HALF' || leaveType === 'HALF_AM' || leaveType === 'HALF_PM';
  const totalCalendarDays = isQuarter ? 0.25 : (isHalf ? 0.5 : getCalendarDays(startDate, endDate));
  const workingDays = isQuarter ? 0.25 : (isHalf ? 0.5 : getWorkingDays(startDate, endDate));

  // Month navigation
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(prev => prev - 1);
    } else {
      setCurrentMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(prev => prev + 1);
    } else {
      setCurrentMonth(prev => prev + 1);
    }
  };

  // Process Receipt Image with Tesseract OCR & Auto-populate Form
  const handleProcessReceiptImage = async (file) => {
    if (!file) return;
    setSelectedFile(file);
    setExpenseFileName(file.name || `receipt_${Date.now()}.jpg`);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Run OCR scan
    setIsOcrScanning(true);
    setOcrProgress(10);
    setOcrNotice(null);

    try {
      const result = await scanReceiptOcr(file, (progress) => {
        setOcrProgress(progress);
      });

      let autoFilled = [];
      if (result.amount) {
        setExpenseAmount(String(result.amount));
        autoFilled.push(`금액: ₩${result.amount.toLocaleString()}`);
      }
      if (result.merchant) {
        setExpenseDetails(result.merchant);
        autoFilled.push(`사용처: ${result.merchant}`);
      }
      if (result.cardCompany) {
        setCardCompany(result.cardCompany);
        autoFilled.push(`카드사: ${result.cardCompany}`);
      }
      if (result.cardNumber) {
        setCardNumber(result.cardNumber);
        autoFilled.push(`카드번호: ${result.cardNumber}`);
      }
      if (result.date) {
        setExpenseDate(result.date);
        autoFilled.push(`일자: ${result.date}`);
      }

      if (autoFilled.length > 0) {
        setOcrNotice({
          type: 'success',
          text: `✨ 영수증 자동 인식 완료 (${autoFilled.join(' | ')})`
        });
      } else {
        setOcrNotice({
          type: 'info',
          text: '영수증 텍스트를 분석했으나 상호명이나 금액을 명확히 특정하지 못했습니다. 수동으로 보완해 주세요.'
        });
      }
    } catch (err) {
      console.warn('OCR Scan failed:', err);
      setOcrNotice({
        type: 'error',
        text: '영수증 OCR 분석 중 오류가 발생했습니다. 직접 입력해 주세요.'
      });
    } finally {
      setIsOcrScanning(false);
      setOcrProgress(0);
    }
  };

  // Webcam Start & Stop Handlers
  const handleStartCamera = async () => {
    // If mobile user agent, trigger native camera input
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && mobileCameraInputRef.current) {
      mobileCameraInputRef.current.click();
      return;
    }

    try {
      setIsCameraModalOpen(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      mediaStreamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.warn('Webcam access error, fallback to file camera input:', err);
      setIsCameraModalOpen(false);
      if (mobileCameraInputRef.current) {
        mobileCameraInputRef.current.click();
      }
    }
  };

  const handleStopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    setIsCameraModalOpen(false);
  };

  const handleCaptureWebcam = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `receipt_cam_${Date.now()}.jpg`, { type: 'image/jpeg' });
        handleStopCamera();
        handleProcessReceiptImage(file);
      }
    }, 'image/jpeg', 0.95);
  };

  // Calendar Day Click Handler
  const handleCalendarDayClick = (dayNum) => {
    if (!dayNum) return;
    const clickedDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    
    if (leaveType === 'HALF') {
      setStartDate(clickedDateStr);
      setEndDate(clickedDateStr);
      return;
    }

    if (isSpecial) {
      if (specialType === '조부상') {
        setStartDate(clickedDateStr);
        setEndDate(clickedDateStr);
        return;
      }
      if (specialType === '부모님상') {
        const start = new Date(clickedDateStr);
        const end = new Date(start);
        end.setDate(start.getDate() + 4); // 5 calendar days
        setStartDate(clickedDateStr);
        setEndDate(end.toISOString().split('T')[0]);
        return;
      }
    }

    // Toggle range selection
    if (!startDate || (startDate && endDate && startDate !== endDate)) {
      setStartDate(clickedDateStr);
      setEndDate(clickedDateStr);
    } else {
      const start = new Date(startDate);
      const clicked = new Date(clickedDateStr);
      
      if (clicked < start) {
        setStartDate(clickedDateStr);
        setEndDate(clickedDateStr);
      } else {
        setEndDate(clickedDateStr);
      }
    }
  };

  // Automatically adjust leave ranges based on presets
  useEffect(() => {
    if (isSpecial) {
      if (specialType === '조부상') {
        setEndDate(startDate);
      } else if (specialType === '부모님상') {
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + 4);
        setEndDate(end.toISOString().split('T')[0]);
      }
    }
  }, [isSpecial, specialType, startDate]);

  // Sidebar action click auto-scroll listeners
  useEffect(() => {
    const handleScrollLeave = () => {
      leaveFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const handleScrollExpense = () => {
      expenseFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    window.addEventListener('trigger-apply-leave', handleScrollLeave);
    window.addEventListener('trigger-apply-expense', handleScrollExpense);

    return () => {
      window.removeEventListener('trigger-apply-leave', handleScrollLeave);
      window.removeEventListener('trigger-apply-expense', handleScrollExpense);
    };
  }, []);

  // Leave Submit with Real S3 Upload for Proof documents
  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingLeave) return;
    if (workingDays <= 0) {
      alert('신청 일수가 0일 이하입니다. 날짜를 확인해 주세요.');
      return;
    }

    // Check Leave Policy restrictions
    if (!isSpecial && userPolicyGroup?.settings?.standard === 'NONE') {
      alert(`현재 소속 정책 그룹('${userPolicyGroup.name}')은 연차 미발생 그룹으로 연차 신청이 불가합니다. 인사 관리자에게 문의하세요.`);
      return;
    }

    // Check remaining balance if not special leave and overuse is prohibited
    if (!isSpecial && userPolicyGroup?.settings?.allowOveruse === false && workingDays > (userDbDetails.RemainingLeave || 0)) {
      alert(`잔여 연차가 부족합니다. 현재 소속 정책('${userPolicyGroup.name}')의 초과 사용 제한 규정에 따릅니다. (잔여: ${userDbDetails.RemainingLeave || 0}일, 신청: ${workingDays}일)`);
      return;
    }

    const specialTypeLabel = isSpecial 
      ? (specialType === '기타' ? customSpecialName || '기타 특수휴가' : specialType) 
      : '';

    setIsSubmittingLeave(true);
    try {
      if (proofFile) {
        setProofUploadProgress(15);
        const response = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/expense/upload', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ApplicantID: currentUser.UserID,
            Type: 'LEAVE',
            Amount: 0,
            FileName: proofFileName
          })
        });

        if (!response.ok) {
          throw new Error('증빙 업로드 Presigned URL 발급 실패');
        }

        const data = await response.json();
        const s3UploadUrl = data.UploadUrl;
        const proofS3Url = data.ReceiptS3Url;
        const approvalId = data.ApprovalID;

        setProofUploadProgress(50);

        const s3Response = await fetch(s3UploadUrl, {
          method: 'PUT',
          body: proofFile,
          headers: {
            'Content-Type': proofFile.type || 'application/octet-stream'
          }
        });

        if (!s3Response.ok) {
          throw new Error('S3 증빙 업로드 실패');
        }

        setProofUploadProgress(100);
        setTimeout(() => setProofUploadProgress(0), 1000);

        applyForLeave(
          workingDays,
          leaveReason,
          startDate,
          isHalf || isQuarter ? startDate : endDate,
          leaveType,
          isSpecial,
          specialTypeLabel,
          proofFileName,
          proofS3Url,
          totalCalendarDays,
          approvalId,
          null,
          sendKakaoLeave
        );
      } else {
        applyForLeave(
          workingDays,
          leaveReason,
          startDate,
          isHalf || isQuarter ? startDate : endDate,
          leaveType,
          isSpecial,
          specialTypeLabel,
          '',
          '',
          totalCalendarDays,
          '',
          null,
          sendKakaoLeave
        );
      }

      setLeaveReason('');
      setProofFile(null);
      setProofFileName('');
      setProofPreviewUrl('');
      setIsSpecial(false);
      alert('연차 및 휴가 결재 신청이 완료되었습니다.');
    } catch (err) {
      alert(`신청 중 오류가 발생했습니다: ${err.message}`);
      setProofUploadProgress(0);
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
      setProofFileName(file.name);
      setProofPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingExpense) return;
    const finalCardCompany = cardCompany === '직접 입력' ? (customCardCompany.trim() || '기타카드') : cardCompany;
    if (!expenseAmount || !expenseFileName || !expenseDetails || !finalCardCompany) {
      alert('모든 필수 필드(금액, 사용처, 영수증)를 입력해 주세요.');
      return;
    }

    const amount = parseFloat(expenseAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('경비 금액을 올바르게 입력해 주세요.');
      return;
    }

    setIsSubmittingExpense(true);
    try {
      setUploadProgress(15);
      const response = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/expense/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ApplicantID: currentUser.UserID,
          Type: 'EXPENSE',
          Amount: amount,
          FileName: expenseFileName
        })
      });

      if (!response.ok) {
        throw new Error('Presigned URL 발급 실패');
      }

      const data = await response.json();
      const s3UploadUrl = data.UploadUrl;
      const receiptS3Url = data.ReceiptS3Url;
      const approvalId = data.ApprovalID;

      setUploadProgress(50);

      const s3Response = await fetch(s3UploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type || 'application/octet-stream'
        }
      });

      if (!s3Response.ok) {
        throw new Error('S3 버킷 업로드 실패');
      }

      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1000);

      const formattedDetails = `[${expenseCategory}] ${expenseDetails}`;

      applyForExpense(
        amount,
        formattedDetails,
        finalCardCompany,
        cardNumber.trim(),
        expenseFileName,
        receiptS3Url,
        approvalId,
        null,
        sendKakaoExpense,
        expenseDate
      );

      setExpenseAmount('');
      setExpenseFileName('');
      setExpenseDetails('');
      setSelectedFile(null);
      setPreviewUrl('');
      setCardNumber('');
      setCustomCardCompany('');
      alert('경비 정산 신청이 정상적으로 완료되었습니다.');
    } catch (err) {
      alert(`업로드 및 신청 중 에러가 발생했습니다: ${err.message}`);
      setUploadProgress(0);
    } finally {
      setIsSubmittingExpense(false);
    }
  };

  // ----------------------------------------------------
  // RENDER VIEW A: LEAVE MODE (Light Modern)
  // ----------------------------------------------------
  if (mode === 'leave') {
    const pendingLeaveCount = leaveApprovals.filter(a => a.Status === 'PENDING').length;
    const pendingLeaveList = leaveApprovals.filter(a => a.Status === 'PENDING');
    const pendingLeaveDays = pendingLeaveList.reduce((sum, a) => sum + (Number(a.LeaveDays) || 0), 0);
    const totalLeave = (userDbDetails.TotalLeave !== undefined && userDbDetails.TotalLeave !== null && userDbDetails.TotalLeave !== '') ? Number(userDbDetails.TotalLeave) : 15;
    const usedLeave = Number(userDbDetails.UsedLeave || 0);
    const baseRemaining = (userDbDetails.RemainingLeave !== undefined && userDbDetails.RemainingLeave !== null && userDbDetails.RemainingLeave !== '') ? Number(userDbDetails.RemainingLeave) : (totalLeave - usedLeave);
    const realRemainingDays = baseRemaining - pendingLeaveDays;
    const requestedDays = isSpecial ? 0 : workingDays;
    const projectedRemainingDays = realRemainingDays - requestedDays;
    const requestedTypeLabel = isSpecial 
      ? (specialType ? `특별(${specialType})` : '특별휴가')
      : (leaveType === 'HALF_AM' ? '오전반차' : leaveType === 'HALF_PM' ? '오후반차' : leaveType === 'HALF' ? '반차' : leaveType === 'QUARTER' ? '반반차' : '연차');

    const lastApprovedLeave = leaveApprovals.find(a => a.Status === 'APPROVED');
    const nextApprovedLeave = leaveApprovals.find(a => a.Status === 'APPROVED' && new Date(a.StartDate) > new Date());
    const firstPendingLeave = leaveApprovals.find(a => a.Status === 'PENDING');

    return (
      <div className="p-4 sm:p-6 md:p-8 bg-slate-50 min-h-[calc(100vh-80px)] text-slate-800 space-y-6">
        
        {/* Page Head */}
        <div className="text-left pb-2">
          <h1 className="text-2xl font-bold text-slate-900">연차 관리</h1>
          <p className="text-xs text-slate-500 mt-1">연차 잔여 일수를 조회하고 휴가 결재를 신청합니다.</p>
        </div>

        {/* Metric Cards (4-Tier Comprehensive) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">총 부여 연차</span>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-3xl font-black text-slate-900">{totalLeave}일</span>
            </div>
            <span className="text-[10px] text-slate-400 font-semibold block mt-2">2026 회계연도 기준</span>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">사용 완료 (기사용)</span>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-3xl font-black text-slate-900">{usedLeave}일</span>
            </div>
            <span className="text-[10px] text-indigo-600 font-bold block mt-2">
              {totalLeave > 0 ? ((usedLeave / totalLeave) * 100).toFixed(1) : 0}% 사용 완료
            </span>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">결재 진행 중 (신청 중)</span>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-3xl font-black text-amber-600">{pendingLeaveDays}일</span>
            </div>
            <span className="text-[10px] text-amber-600 font-semibold block mt-2">
              {pendingLeaveList.length}건 관리자 검토 대기
            </span>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left ring-2 ring-emerald-500/20 bg-gradient-to-b from-white to-emerald-50/20">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">실사용 가능 잔여</span>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-3xl font-black text-emerald-600">{realRemainingDays.toFixed(1)}일</span>
            </div>
            <span className="text-[10px] text-emerald-600 font-semibold block mt-2">
              총부여({totalLeave}) - 사용({usedLeave}) - 대기({pendingLeaveDays})
            </span>
          </div>
        </div>

        {/* Form & Status Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left panel: Leave Usage Card */}
          <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs flex flex-col justify-between space-y-6 text-left">
            <div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-sm font-bold text-slate-900">연차 사용 현황</h3>
                <span className="text-xs text-slate-500 font-semibold">
                  {userDbDetails.UsedLeave} / {userDbDetails.TotalLeave} 일
                </span>
              </div>

              {/* Progress bar */}
              <div className="mt-4 space-y-1.5">
                <div className="flex justify-between text-[11px] font-bold text-slate-500">
                  <span>사용률</span>
                  <span>{((userDbDetails.UsedLeave / userDbDetails.TotalLeave) * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, Math.max(0, (userDbDetails.UsedLeave / userDbDetails.TotalLeave) * 100))}%` }}
                  />
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="mt-6 flex bg-slate-100 p-1 rounded-xl border border-slate-200 w-fit">
                <button
                  onClick={() => setLeaveFilter('ALL')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    leaveFilter === 'ALL' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  전체
                </button>
                <button
                  onClick={() => setLeaveFilter('FULL')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    leaveFilter === 'FULL' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  연차
                </button>
                <button
                  onClick={() => setLeaveFilter('HALF')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    leaveFilter === 'HALF' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  반차
                </button>
                <button
                  onClick={() => setLeaveFilter('SPECIAL')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    leaveFilter === 'SPECIAL' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  기타(특별)
                </button>
              </div>
            </div>

            {/* Dynamic recent logs text display */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-2 leading-relaxed text-left">
              <div>
                <strong>• 최근 사용: </strong>
                <span>
                  {lastApprovedLeave 
                    ? `${lastApprovedLeave.StartDate} [${lastApprovedLeave.LeaveType === 'HALF' ? '반차' : '연차'}] (${lastApprovedLeave.Details})`
                    : '이력이 없습니다.'}
                </span>
              </div>
              <div>
                <strong>• 예정: </strong>
                <span>
                  {nextApprovedLeave 
                    ? `${nextApprovedLeave.StartDate} [${nextApprovedLeave.LeaveType === 'HALF' ? '반차' : '연차'}] (${nextApprovedLeave.Details})`
                    : '예정된 일정이 없습니다.'}
                </span>
              </div>
              <div>
                <strong>• 승인 대기: </strong>
                <span>
                  {firstPendingLeave 
                    ? `${firstPendingLeave.StartDate} [${firstPendingLeave.LeaveType === 'HALF' ? '반차' : '연차'}] (${firstPendingLeave.Details})`
                    : '대기 중인 건이 없습니다.'}
                </span>
              </div>
            </div>
          </section>

          {/* Right panel: Leave Request Form */}
          <section ref={leaveFormRef} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">연차 / 휴가 결재 신청</h3>
            </div>

            <form onSubmit={handleLeaveSubmit} className="space-y-4">
              {/* Special Leave checkbox */}
              <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
                <div className="text-left">
                  <span className="text-xs font-bold text-slate-800 block">연차 차감 제외 신청</span>
                  <span className="text-[10px] text-slate-500">경조사 및 포상휴가 등은 연차를 차감하지 않습니다.</span>
                </div>
                <input
                  type="checkbox"
                  checked={isSpecial}
                  onChange={(e) => {
                    setIsSpecial(e.target.checked);
                    if (e.target.checked) setLeaveType('FULL');
                  }}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                />
              </div>

              {/* Special Leave Presets */}
              {isSpecial && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-indigo-700 uppercase">특수 휴가 유형</label>
                    <select
                      value={specialType}
                      onChange={(e) => setSpecialType(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:outline-none focus:border-indigo-600 text-slate-800"
                    >
                      <option value="조부상">조부상 (1일)</option>
                      <option value="부모님상">부모님상 (5일)</option>
                      <option value="포상휴가">포상휴가</option>
                      <option value="기타">기타 (직접 입력)</option>
                    </select>
                  </div>
                  
                  {specialType === '기타' && (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-indigo-700 uppercase">휴가 사유 직접 입력</label>
                      <input
                        type="text"
                        placeholder="예: 본인 결혼, 병가 등"
                        value={customSpecialName}
                        onChange={(e) => setCustomSpecialName(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:outline-none focus:border-indigo-600 text-slate-800"
                        required
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Standard leave selector with Policy Group Integration */}
              {!isSpecial && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold text-slate-600 uppercase">신청 구분</label>
                    {userPolicyGroup && (
                      <span className="text-[10px] text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded-md font-bold border border-cyan-200">
                        적용 정책: {userPolicyGroup.name}
                      </span>
                    )}
                  </div>

                  {userPolicyGroup?.settings?.standard === 'NONE' ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 font-medium">
                      ⚠️ 현재 소속 정책 그룹('연차 미발생 그룹')에 따라 연차가 부여되지 않는 계정입니다.
                    </div>
                  ) : (() => {
                    const allowQuarter = (userPolicyGroup?.settings?.unit === '0.25') && userPolicyGroup?.settings?.allowPartial;
                    const allowHalf = (userPolicyGroup?.settings?.unit === '0.25' || userPolicyGroup?.settings?.unit === '0.5') && userPolicyGroup?.settings?.allowPartial;
                    const gridColsClass = allowQuarter ? 'grid-cols-3' : (allowHalf ? 'grid-cols-2' : 'grid-cols-1');

                    return (
                      <div className="space-y-2">
                        <div className={`grid ${gridColsClass} gap-2`}>
                          <button
                            type="button"
                            onClick={() => { setLeaveType('FULL'); setEndDate(startDate); }}
                            className={`py-2.5 px-3 rounded-xl font-bold text-xs transition-all cursor-pointer text-center ${
                              leaveType === 'FULL'
                                ? 'bg-indigo-600 text-white shadow-xs'
                                : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            연차 (1일)
                          </button>
                          {allowHalf && (
                            <button
                              type="button"
                              onClick={() => { 
                                setLeaveType(prev => (prev === 'HALF_PM' ? 'HALF_PM' : 'HALF_AM')); 
                                setEndDate(startDate); 
                              }}
                              className={`py-2.5 px-3 rounded-xl font-bold text-xs transition-all cursor-pointer text-center ${
                                isHalf
                                  ? 'bg-indigo-600 text-white shadow-xs'
                                  : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              반차 (0.5일)
                            </button>
                          )}
                          {allowQuarter && (
                            <button
                              type="button"
                              onClick={() => { setLeaveType('QUARTER'); setEndDate(startDate); }}
                              className={`py-2.5 px-3 rounded-xl font-bold text-xs transition-all cursor-pointer text-center ${
                                leaveType === 'QUARTER'
                                  ? 'bg-cyan-600 text-white shadow-xs font-black'
                                  : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              반반차 (0.25일)
                            </button>
                          )}
                        </div>

                        {/* Sub-selection for Half Day: AM / PM */}
                        {isHalf && (
                          <div className="p-2.5 bg-indigo-50/70 rounded-xl border border-indigo-100 flex items-center justify-around gap-2 text-xs animate-in fade-in">
                            <label className={`flex items-center gap-1.5 font-bold cursor-pointer py-1.5 px-3 rounded-lg transition-all ${leaveType === 'HALF_AM' || leaveType === 'HALF' ? 'bg-white text-indigo-700 shadow-xs border border-indigo-200' : 'text-slate-600 hover:text-indigo-600'}`}>
                              <input
                                type="radio"
                                name="pcHalfType"
                                value="HALF_AM"
                                checked={leaveType === 'HALF_AM' || leaveType === 'HALF'}
                                onChange={() => setLeaveType('HALF_AM')}
                                className="w-3.5 h-3.5 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span>☀️ 오전반차 (09:00 ~ 13:00)</span>
                            </label>
                            <label className={`flex items-center gap-1.5 font-bold cursor-pointer py-1.5 px-3 rounded-lg transition-all ${leaveType === 'HALF_PM' ? 'bg-white text-indigo-700 shadow-xs border border-indigo-200' : 'text-slate-600 hover:text-indigo-600'}`}>
                              <input
                                type="radio"
                                name="pcHalfType"
                                value="HALF_PM"
                                checked={leaveType === 'HALF_PM'}
                                onChange={() => setLeaveType('HALF_PM')}
                                className="w-3.5 h-3.5 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span>🌙 오후반차 (14:00 ~ 18:00)</span>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Interactive Calendar widget */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase flex items-center gap-1">
                  달력 일자 지정 (사용일을 더블클릭/드래그 하세요)
                </label>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                  <div className="flex justify-between items-center mb-3">
                    <button
                      type="button"
                      onClick={handlePrevMonth}
                      className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-100 rounded text-slate-600 text-xs font-bold"
                    >
                      이전달
                    </button>
                    <span className="text-xs font-bold text-slate-800">
                      {currentYear}년 {currentMonth + 1}월
                    </span>
                    <button
                      type="button"
                      onClick={handleNextMonth}
                      className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-100 rounded text-slate-600 text-xs font-bold"
                    >
                      다음달
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-500 mb-2">
                    <span className="text-rose-500">일</span>
                    <span>월</span>
                    <span>화</span>
                    <span>수</span>
                    <span>목</span>
                    <span>금</span>
                    <span className="text-indigo-600">토</span>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: new Date(currentYear, currentMonth, 1).getDay() }).map((_, i) => (
                      <div key={`pad-${i}`} className="h-8" />
                    ))}
                    
                    {Array.from({ length: new Date(currentYear, currentMonth + 1, 0).getDate() }).map((_, i) => {
                      const day = i + 1;
                      const thisDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      
                      const isStart = startDate === thisDateStr;
                      const isEnd = endDate === thisDateStr;
                      
                      const startDt = new Date(startDate);
                      const endDt = new Date(endDate);
                      const curDt = new Date(thisDateStr);
                      const isWithinRange = startDate && endDate && curDt >= startDt && curDt <= endDt;
                      
                      const dayOfWeek = curDt.getDay();
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                      let cellStyle = "h-8 text-xs font-bold rounded-lg cursor-pointer transition-all flex items-center justify-center ";
                      if (isStart || isEnd) {
                        cellStyle += "bg-indigo-600 text-white shadow-xs";
                      } else if (isWithinRange) {
                        cellStyle += "bg-indigo-100 text-indigo-800";
                      } else {
                        cellStyle += "bg-white hover:bg-slate-200 text-slate-700 border border-slate-200/60 ";
                        if (isWeekend) {
                          cellStyle += dayOfWeek === 0 ? "text-rose-500" : "text-indigo-600";
                        }
                      }

                      return (
                        <button
                          key={`day-btn-${day}`}
                          type="button"
                          onClick={() => handleCalendarDayClick(day)}
                          className={cellStyle}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Leave Days computation summary */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-medium">총 휴가 기간 (달력일)</span>
                  <span className="text-indigo-700 font-bold">{totalCalendarDays} 일</span>
                </div>
                <div className="flex justify-between items-center border-l border-slate-200 pl-4">
                  <span className="text-slate-500 font-medium">실제 연차 차감 (영업일)</span>
                  <span className={`font-bold ${isSpecial ? 'text-emerald-600 line-through' : 'text-indigo-700'}`}>
                    {isSpecial ? '0일 (차감제외)' : `${workingDays} 일`}
                  </span>
                </div>
              </div>

              {/* Real-time Leave Balance Calculation Formula Banner */}
              <div className="p-3.5 bg-gradient-to-r from-indigo-50/90 via-sky-50/80 to-emerald-50/70 rounded-2xl border border-indigo-100 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-extrabold text-slate-800 flex items-center gap-1.5">
                    <span>📊</span> 연차 실시간 차감 계산
                  </span>
                  <span className="text-[11px] font-bold text-indigo-700 bg-white/90 px-2 py-0.5 rounded-md border border-indigo-200/60 shadow-2xs">
                    {isSpecial ? '특별휴가 (연차 미차감)' : `${requestedTypeLabel} (${requestedDays}일 차감)`}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs bg-white/95 p-3 rounded-xl border border-indigo-100/70 shadow-2xs">
                  <div className="text-center">
                    <span className="text-[10px] text-slate-500 font-bold block mb-0.5">현재 실질 잔여</span>
                    <span className="font-black text-sm text-slate-900">{realRemainingDays.toFixed(1)}일</span>
                  </div>
                  <div className="text-center border-x border-slate-100">
                    <span className="text-[10px] text-indigo-600 font-bold block mb-0.5">이번 신청</span>
                    <span className="font-black text-sm text-indigo-600">-{requestedDays}일</span>
                  </div>
                  <div className="text-center">
                    <span className="text-[10px] text-emerald-600 font-bold block mb-0.5">신청 후 예상 잔여</span>
                    <span className={`font-black text-sm ${projectedRemainingDays < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {projectedRemainingDays.toFixed(1)}일
                    </span>
                  </div>
                </div>
                {projectedRemainingDays < 0 && !isSpecial && (
                  <p className="text-[11px] text-rose-600 font-bold text-left px-1">
                    ⚠️ 잔여 연차를 초과하여 신청할 수 없습니다. 일수를 조정해 주세요.
                  </p>
                )}
              </div>

              {/* Proof Document attachment */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase block">증빙 첨부파일 (경조사 등 증빙 필수)</label>
                <input
                  type="file"
                  ref={proofFileInputRef}
                  onChange={handleProofFileChange}
                  accept="image/*,application/pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => proofFileInputRef.current.click()}
                  className="w-full bg-white border border-slate-300 hover:border-slate-400 rounded-xl py-2.5 px-4 text-xs font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition-all cursor-pointer text-slate-700"
                >
                  <Upload className="w-4 h-4 text-indigo-600" />
                  증빙 서류 파일 선택 (PDF / 이미지)
                </button>

                {proofFileName && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileCheck className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      <div className="text-left overflow-hidden">
                        <span className="text-xs font-bold text-slate-800 block truncate">{proofFileName}</span>
                        <span className="text-[10px] text-slate-500">증빙이 결재서류와 함께 제출됩니다.</span>
                      </div>
                    </div>
                    {proofPreviewUrl && proofFile?.type?.startsWith('image/') && (
                      <img src={proofPreviewUrl} alt="Proof preview" className="w-10 h-10 object-cover rounded-lg border border-slate-200" />
                    )}
                  </div>
                )}

                {proofUploadProgress > 0 && (
                  <div className="space-y-1.5 bg-slate-50 p-3 rounded-xl border border-slate-200">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-indigo-600">증빙 파일 S3 업로드 중...</span>
                      <span>{proofUploadProgress}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div
                        className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${proofUploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Leave reason */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase">사유 *</label>
                <input
                  type="text"
                  placeholder="간단한 사유를 입력하세요"
                  value={leaveReason}
                  onChange={e => setLeaveReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs focus:outline-none focus:border-indigo-600 text-slate-900"
                  required
                />
              </div>

              {/* KakaoTalk Notification Toggle (Default ON) */}
              <div 
                id="kakao-leave-toggle-btn"
                onClick={() => setSendKakaoLeave(prev => !prev)}
                className="bg-amber-50/80 border border-amber-200/90 p-3 rounded-2xl flex items-center justify-between shadow-2xs cursor-pointer hover:bg-amber-100/60 transition-all select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-amber-400 text-slate-950 flex items-center justify-center font-bold shadow-2xs flex-shrink-0">
                    <MessageSquare className="w-3.5 h-3.5 text-slate-900" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-900 block">결재 담당자에게 카카오톡 승인 요청 알림 전송</span>
                    <span className="text-[11px] text-slate-600">신청 즉시 1차 결재자(팀장/임원)에게 카카오톡으로 결재 대기 알림과 바로가기를 발송합니다.</span>
                  </div>
                </div>
                <div className="relative inline-flex items-center flex-shrink-0 ml-3">
                  <input
                    type="checkbox"
                    checked={sendKakaoLeave}
                    onChange={e => setSendKakaoLeave(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-9 h-5 rounded-full transition-all relative ${sendKakaoLeave ? 'bg-amber-500' : 'bg-slate-300'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-[2px] transition-all shadow-xs ${sendKakaoLeave ? 'right-[2px]' : 'left-[2px]'}`} />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setLeaveReason('');
                    setProofFile(null);
                    setProofFileName('');
                    setIsSpecial(false);
                  }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer"
                >
                  취소
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmittingLeave}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingLeave ? '신청 중...' : '신청하기'}
                </button>
              </div>
            </form>
          </section>
        </div>

        {/* Bottom Panel: History log */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs overflow-hidden text-left space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <span>신청 및 사용 내역</span>
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100 font-mono">
                  {filteredLeaveApprovals.length}건
                </span>
              </h3>
              <span className="text-[11px] text-slate-400 font-medium">나의 연차/휴가 신청 및 결재 승인 이력을 조회합니다.</span>
            </div>

            {/* Search Bar */}
            <div className="relative w-full md:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="사유, 반려 사유, 일자 검색..."
                value={leaveSearchTerm}
                onChange={(e) => {
                  setLeaveSearchTerm(e.target.value);
                  setLeavePage(1);
                }}
                className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600 focus:bg-white font-medium transition-all"
              />
              {leaveSearchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setLeaveSearchTerm('');
                    setLeavePage(1);
                  }}
                  className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Period Filter Buttons for Leave */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { id: 'ALL', label: '전체 내역' },
                { id: 'THIS_MONTH', label: '이번 달 (당월)' },
                { id: '3_MONTHS', label: '최근 3개월' },
                { id: '6_MONTHS', label: '최근 6개월' },
                { id: 'CUSTOM', label: '직접 기간 선택' }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setLeavePeriodFilter(tab.id);
                    setLeavePage(1);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    leavePeriodFilter === tab.id
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Custom Date Range Picker */}
            {leavePeriodFilter === 'CUSTOM' && (
              <div className="flex items-center gap-2 animate-fade-in text-xs">
                <input
                  type="date"
                  value={leaveCustomStartDate}
                  onChange={(e) => {
                    setLeaveCustomStartDate(e.target.value);
                    setLeavePage(1);
                  }}
                  className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-600"
                />
                <span className="text-slate-400 font-bold">~</span>
                <input
                  type="date"
                  value={leaveCustomEndDate}
                  onChange={(e) => {
                    setLeaveCustomEndDate(e.target.value);
                    setLeavePage(1);
                  }}
                  className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-600"
                />
              </div>
            )}
          </div>

          {/* Mobile Card List */}
          <div className="block md:hidden space-y-3">
            {filteredLeaveApprovals.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-2xl border border-slate-100">
                선택한 조건의 연차 신청 내역이 없습니다.
              </div>
            ) : (
              filteredLeaveApprovals.slice((leavePage - 1) * ITEMS_PER_PAGE, leavePage * ITEMS_PER_PAGE).map(appr => (
                <div key={appr.ApprovalID} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5 text-left shadow-2xs">
                  <div className="flex justify-between items-start">
                    {appr.IsSpecial ? (
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-emerald-100 text-emerald-800">
                        특별({appr.SpecialType})
                      </span>
                    ) : appr.LeaveType === 'HALF_AM' ? (
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-sky-100 text-sky-800 flex items-center gap-1">
                        <span>☀️</span> 오전반차
                      </span>
                    ) : appr.LeaveType === 'HALF_PM' ? (
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-amber-100 text-amber-800 flex items-center gap-1">
                        <span>🌙</span> 오후반차
                      </span>
                    ) : appr.LeaveType === 'HALF' ? (
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-indigo-100 text-indigo-800">
                        반차
                      </span>
                    ) : appr.LeaveType === 'QUARTER' ? (
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-teal-100 text-teal-800">
                        반반차
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-indigo-100 text-indigo-800">
                        연차
                      </span>
                    )}
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        appr.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                        appr.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                        appr.Status === 'CANCEL_REQUESTED' ? 'bg-amber-100 text-amber-800' :
                        appr.Status === 'CANCELED' ? 'bg-slate-100 text-slate-500' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {appr.Status === 'APPROVED' ? '승인 완료' : 
                         appr.Status === 'REJECTED' ? '반려됨' : 
                         appr.Status === 'CANCEL_REQUESTED' ? '취소 대기' :
                         appr.Status === 'CANCELED' ? '취소 완료' :
                         '승인 대기'}
                      </span>
                      {appr.Status === 'REJECTED' && appr.RejectReason && (
                        <span className="text-[10px] text-rose-700 font-bold bg-rose-50 px-2 py-0.5 rounded border border-rose-100 block">
                          사유: {appr.RejectReason}
                        </span>
                      )}
                      {appr.Status === 'PENDING' && (
                        <button
                          onClick={() => {
                            if (confirm('대기 중인 연차 신청을 회수하시겠습니까?')) {
                              withdrawLeaveRequest(appr.ApprovalID);
                            }
                          }}
                          className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded text-[10px] font-bold mt-1 transition-all cursor-pointer"
                        >
                          신청 회수
                        </button>
                      )}
                      {appr.Status === 'APPROVED' && (
                        appr.StartDate >= todayStr ? (
                          <button
                            onClick={() => {
                              setCancelModalItem(appr);
                              setCancelReasonInput('');
                            }}
                            className="px-2 py-0.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded text-[10px] font-bold mt-1 transition-all cursor-pointer"
                          >
                            취소 신청
                          </button>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-400 rounded text-[10px] font-bold mt-1 inline-block">
                            사용완료
                          </span>
                        )
                      )}
                      <button
                        onClick={() => setSelectedLeaveDetail(appr)}
                        className="px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded text-[10px] font-bold mt-1 transition-all cursor-pointer"
                      >
                        상세보기
                      </button>
                    </div>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-900 block">
                      {appr.StartDate}{appr.StartDate !== appr.EndDate && ` ~ ${appr.EndDate}`}
                    </strong>
                    <span className="text-xs text-slate-500">영업일: {appr.Days}일</span>
                  </div>
                  <p className="text-xs text-slate-700 bg-white p-2.5 rounded-xl border border-slate-200">{appr.Details}</p>
                  <div className="pt-1 flex items-center justify-between">
                    {appr.ProofUrl ? (
                      <a href={appr.ProofUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 font-bold hover:underline">
                        증빙 문서 확인 ❯
                      </a>
                    ) : <span></span>}
                    <button
                      onClick={() => setSelectedLeaveDetail(appr)}
                      className="text-xs text-indigo-600 font-bold hover:underline"
                    >
                      결재 및 연차 상세 ❯
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-700">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 rounded-l-lg whitespace-nowrap">신청일</th>
                  <th className="px-4 py-3 whitespace-nowrap">사용일(기간)</th>
                  <th className="px-4 py-3 whitespace-nowrap">유형</th>
                  <th className="px-4 py-3">사유</th>
                  <th className="px-4 py-3 whitespace-nowrap">증빙</th>
                  <th className="px-4 py-3 rounded-r-lg whitespace-nowrap">상태 및 관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeaveApprovals.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-slate-400">선택한 조건의 연차 신청 내역이 없습니다.</td>
                  </tr>
                ) : (
                  filteredLeaveApprovals.slice((leavePage - 1) * ITEMS_PER_PAGE, leavePage * ITEMS_PER_PAGE).map(appr => (
                    <tr key={appr.ApprovalID} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(appr.CreatedAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">
                        {appr.StartDate}
                        {appr.StartDate !== appr.EndDate && ` ~ ${appr.EndDate}`}
                        <span className="text-xs text-slate-500 font-normal ml-1.5 block sm:inline">
                          ({appr.Days}일 영업일)
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {appr.IsSpecial ? (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            특별({appr.SpecialType})
                          </span>
                        ) : appr.LeaveType === 'HALF_AM' ? (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
                            <span>☀️</span> 오전반차
                          </span>
                        ) : appr.LeaveType === 'HALF_PM' ? (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                            <span>🌙</span> 오후반차
                          </span>
                        ) : appr.LeaveType === 'HALF' ? (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            반차
                          </span>
                        ) : appr.LeaveType === 'QUARTER' ? (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">
                            반반차
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            연차
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-800">{appr.Details}</td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">
                        {appr.ProofUrl ? (
                          <a href={appr.ProofUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline font-bold">
                            문서 열기
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                       <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold text-center ${
                            appr.Status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                            appr.Status === 'REJECTED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                            appr.Status === 'CANCEL_REQUESTED' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            appr.Status === 'CANCELED' ? 'bg-slate-100 text-slate-500 border border-slate-200' :
                            'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                            {appr.Status === 'APPROVED' ? '승인 완료' : 
                             appr.Status === 'REJECTED' ? '반려됨' : 
                             appr.Status === 'CANCEL_REQUESTED' ? '취소 대기' :
                             appr.Status === 'CANCELED' ? '취소 완료' :
                             '승인 대기'}
                          </span>
                          {appr.Status === 'REJECTED' && appr.RejectReason && (
                            <span className="text-[10px] text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-100 font-semibold block" title={appr.RejectReason}>
                              사유: {appr.RejectReason}
                            </span>
                          )}
                          <div className="flex items-center gap-1 mt-0.5">
                            {appr.Status === 'PENDING' && (
                              <button
                                onClick={() => {
                                  if (confirm('대기 중인 연차 신청을 회수하시겠습니까?')) {
                                    withdrawLeaveRequest(appr.ApprovalID);
                                  }
                                }}
                                className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded text-[10px] font-bold transition-all cursor-pointer"
                              >
                                회수
                              </button>
                            )}
                            {appr.Status === 'APPROVED' && (
                              appr.StartDate >= todayStr ? (
                                <button
                                  onClick={() => {
                                    setCancelModalItem(appr);
                                    setCancelReasonInput('');
                                  }}
                                  className="px-2 py-0.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded text-[10px] font-bold transition-all cursor-pointer"
                                >
                                  취소
                                </button>
                              ) : (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-400 rounded text-[10px] font-bold">
                                  사용완료
                                </span>
                              )
                            )}
                            <button
                              onClick={() => setSelectedLeaveDetail(appr)}
                              className="px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded text-[10px] font-bold transition-all cursor-pointer"
                            >
                              상세보기
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination for User Leave History */}
          {filteredLeaveApprovals.length > ITEMS_PER_PAGE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200 text-xs">
              <span className="text-slate-500 font-medium">
                전체 <strong className="text-slate-900 font-bold">{filteredLeaveApprovals.length.toLocaleString()}</strong>건 중{' '}
                <strong className="text-indigo-600 font-bold">{(leavePage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(leavePage * ITEMS_PER_PAGE, filteredLeaveApprovals.length)}</strong>건 표시 (페이지 {leavePage} / {Math.ceil(filteredLeaveApprovals.length / ITEMS_PER_PAGE)})
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setLeavePage(prev => Math.max(1, prev - 1))}
                  disabled={leavePage === 1}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
                >
                  ‹ 이전
                </button>

                {Array.from({ length: Math.ceil(filteredLeaveApprovals.length / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setLeavePage(p)}
                    className={`w-7 h-7 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                      leavePage === p
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => setLeavePage(prev => Math.min(Math.ceil(filteredLeaveApprovals.length / ITEMS_PER_PAGE), prev + 1))}
                  disabled={leavePage === Math.ceil(filteredLeaveApprovals.length / ITEMS_PER_PAGE)}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
                >
                  다음 ›
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Selected Leave Detail Modal for Applicant (Requirement) */}
        {selectedLeaveDetail && (() => {
          const appr = selectedLeaveDetail;
          const isSpecial = Boolean(appr.IsSpecial);
          const reqDays = Number(appr.LeaveDays || appr.Days || 0);

          const otherPendingList = (approvals || []).filter(a => 
            a.Type === 'LEAVE' && 
            a.Status === 'PENDING' && 
            a.ApprovalID !== appr.ApprovalID &&
            (a.ApplicantID === currentUser.UserID || a.ApplicantName === currentUser.Name)
          );
          const otherPendingDays = otherPendingList.reduce((s, a) => s + (Number(a.LeaveDays || a.Days) || 0), 0);
          const allPendingDays = otherPendingDays + (appr.Status === 'PENDING' ? reqDays : 0);

          const isApproved = appr.Status === 'APPROVED';
          const beforeBalance = appr.BeforeLeaveBalance !== undefined
            ? Number(appr.BeforeLeaveBalance)
            : (isApproved
                ? (baseRemaining + (isSpecial ? 0 : reqDays))
                : (baseRemaining - otherPendingDays));

          const afterBalance = appr.AfterLeaveBalance !== undefined
            ? Number(appr.AfterLeaveBalance)
            : (isApproved
                ? baseRemaining
                : (isSpecial ? beforeBalance : (beforeBalance - reqDays)));

          const isCancelReq = appr.Status === 'CANCEL_REQUESTED' || appr.Status === 'CANCELED';
          const restoredBalance = realRemainingDays + reqDays;

          const stages = (appr.ApprovalStages && appr.ApprovalStages.length > 0)
            ? appr.ApprovalStages
            : (typeof getAutoApprovalLine === 'function' ? getAutoApprovalLine({ UserID: appr.ApplicantID, Department: appr.ApplicantDept || '클라우드네이티브' }) : []);

          const leaveTypeName = isSpecial ? (appr.SpecialType || '특별휴가') : (
            appr.LeaveType === 'HALF_AM' ? '오전반차 (09:00~13:00)' :
            appr.LeaveType === 'HALF_PM' ? '오후반차 (14:00~18:00)' :
            appr.LeaveType === 'HALF' ? '반차 (0.5일)' :
            appr.LeaveType === 'QUARTER' ? '반반차 (0.25일)' : '연차'
          );

          return (
            <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-150 flex flex-col max-h-[90vh]">
                
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 px-6 py-4 flex items-center justify-between text-white">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🌴</span>
                    <div>
                      <h3 className="font-extrabold text-base text-white">연차 신청 상세 및 결재 현황</h3>
                      <p className="text-xs text-indigo-200">Leave Application Details & Approval Pipeline</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedLeaveDetail(null)}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto space-y-4 flex-1 text-left text-xs">
                  
                  {/* 4-Tier Applicant Leave Overview */}
                  <div className="space-y-1.5">
                    <span className="font-extrabold text-slate-700 text-xs flex items-center gap-1">
                      <span>👤</span> 내 연차 현황 요약
                    </span>
                    <div className="grid grid-cols-4 gap-1.5 text-center">
                      <div className="bg-slate-50 p-2 rounded-xl border border-slate-200">
                        <span className="text-[9px] font-bold text-slate-400 block mb-0.5">총 부여</span>
                        <span className="font-black text-xs text-slate-900">{totalLeave}일</span>
                      </div>
                      <div className="bg-slate-50 p-2 rounded-xl border border-slate-200">
                        <span className="text-[9px] font-bold text-slate-400 block mb-0.5">기사용</span>
                        <span className="font-black text-xs text-slate-900">{usedLeave}일</span>
                      </div>
                      <div className="bg-amber-50 p-2 rounded-xl border border-amber-200">
                        <span className="text-[9px] font-bold text-amber-600 block mb-0.5">결재대기</span>
                        <span className="font-black text-xs text-amber-600">{allPendingDays}일</span>
                      </div>
                      <div className="bg-emerald-50 p-2 rounded-xl border border-emerald-200">
                        <span className="text-[9px] font-bold text-emerald-700 block mb-0.5">실질잔여</span>
                        <span className="font-black text-xs text-emerald-600">{realRemainingDays.toFixed(1)}일</span>
                      </div>
                    </div>
                  </div>

                  {/* Real-time / Snapshot Calculation Banner */}
                  {isCancelReq ? (
                    <div className="p-3 bg-gradient-to-r from-emerald-50 via-teal-50 to-sky-50 rounded-2xl border border-emerald-200 space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-extrabold text-emerald-900 flex items-center gap-1">
                          <span>🔄</span> 연차 취소 시 복구 계산
                        </span>
                        <span className="text-[10px] font-bold text-emerald-700 bg-white px-2 py-0.5 rounded-md border border-emerald-200">
                          +{reqDays}일 연차 환원
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5 bg-white p-2 rounded-xl border border-emerald-100 text-center">
                        <div>
                          <span className="text-[9px] text-slate-400 font-bold block">현재 잔여</span>
                          <span className="font-black text-xs text-slate-900">{realRemainingDays.toFixed(1)}일</span>
                        </div>
                        <div className="border-x border-slate-100">
                          <span className="text-[9px] text-emerald-600 font-bold block">취소 환원</span>
                          <span className="font-black text-xs text-emerald-600">+{reqDays}일</span>
                        </div>
                        <div>
                          <span className="text-[9px] text-emerald-700 font-bold block">복구 후 잔여</span>
                          <span className="font-black text-xs text-emerald-700">{restoredBalance.toFixed(1)}일</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 rounded-2xl border border-indigo-100 space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-extrabold text-indigo-950 flex items-center gap-1">
                          <span>📊</span> 연차 차감 계산 내역
                        </span>
                        <span className="text-[10px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded-md border border-indigo-200">
                          {isSpecial ? '특별휴가 (차감 없음)' : `-${reqDays}일 차감`}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5 bg-white p-2 rounded-xl border border-indigo-100 text-center">
                        <div>
                          <span className="text-[9px] text-slate-400 font-bold block">신청 전 잔여</span>
                          <span className="font-black text-xs text-slate-900">{beforeBalance.toFixed(1)}일</span>
                        </div>
                        <div className="border-x border-slate-100">
                          <span className="text-[9px] text-indigo-600 font-bold block">이번 신청</span>
                          <span className="font-black text-xs text-indigo-600">{isSpecial ? '0일' : `-${reqDays}일`}</span>
                        </div>
                        <div>
                          <span className="text-[9px] text-emerald-600 font-bold block">승인 후 최종 잔여</span>
                          <span className={`font-black text-xs ${afterBalance < 0 && !isSpecial ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {afterBalance.toFixed(1)}일
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Application Info Box */}
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 space-y-2">
                    <div className="flex justify-between items-center pb-1.5 border-b border-slate-200/60">
                      <span className="font-bold text-slate-500">휴가 유형</span>
                      <span className="font-extrabold text-indigo-700 bg-white px-2.5 py-0.5 rounded-md border border-slate-200">
                        {leaveTypeName}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pb-1.5 border-b border-slate-200/60">
                      <span className="font-bold text-slate-500">신청 기간</span>
                      <span className="font-extrabold text-slate-800">
                        {appr.StartDate} {appr.StartDate !== appr.EndDate && `~ ${appr.EndDate}`} ({reqDays}일 차감)
                      </span>
                    </div>
                    <div className="space-y-1 pt-1">
                      <span className="font-bold text-slate-500 block">신청 사유</span>
                      <p className="bg-white p-2.5 rounded-xl border border-slate-200 text-slate-700 whitespace-pre-wrap leading-relaxed font-medium">
                        {appr.Details || appr.Reason}
                      </p>
                    </div>
                  </div>

                  {/* Approval Pipeline */}
                  <div className="space-y-2 pt-1">
                    <span className="font-extrabold text-slate-700 text-xs flex items-center gap-1">
                      <span>📑</span> 다단계 결재선 현황
                    </span>
                    <div className="relative pl-6 space-y-3.5 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                      {stages.map((stage, sIdx) => {
                        const isDone = stage.Status === 'APPROVED' || (appr.Status === 'APPROVED' && (stage.Step || sIdx + 1) <= (appr.CurrentStep || 1));
                        const isRejected = stage.Status === 'REJECTED' || appr.Status === 'REJECTED';
                        const isCurrent = appr.Status === 'PENDING' && Number(appr.CurrentStep || 1) === (stage.Step || sIdx + 1);

                        return (
                          <div key={sIdx} className="relative flex items-start gap-2.5">
                            <div className={`absolute -left-6 top-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shadow-xs ${
                              isDone ? 'bg-emerald-500 text-white' :
                              isRejected ? 'bg-rose-500 text-white' :
                              isCurrent ? 'bg-indigo-600 text-white animate-pulse' :
                              'bg-slate-200 text-slate-500'
                            }`}>
                              {isDone ? '✓' : isRejected ? '✕' : stage.Step || sIdx + 1}
                            </div>
                            <div className="flex-1 bg-slate-50 p-2.5 rounded-xl border border-slate-200/80">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-slate-900">
                                  {stage.ApproverName || `결재자 ${sIdx + 1}단계`} ({stage.ApproverJobTitle || '결재자'})
                                </span>
                                <span className={`text-[9px] font-extrabold px-1.5 py-0.2 rounded-full ${
                                  isDone ? 'bg-emerald-100 text-emerald-800' :
                                  isRejected ? 'bg-rose-100 text-rose-800' :
                                  isCurrent ? 'bg-amber-100 text-amber-800' :
                                  'bg-slate-100 text-slate-400'
                                }`}>
                                  {isDone ? '승인 완료' : isRejected ? '반려' : isCurrent ? '대기 중' : '대기'}
                                </span>
                              </div>
                              {stage.ActionAt && (
                                <span className="text-[10px] text-slate-400 font-mono block mt-0.5">
                                  서명 일시: {new Date(stage.ActionAt).toLocaleString()}
                                </span>
                              )}
                              {stage.Comment && (
                                <p className="text-[11px] text-slate-600 bg-white p-1.5 rounded-lg border border-slate-200 mt-1 italic">
                                  "{stage.Comment}"
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Proof Document */}
                  {appr.ProofUrl && (
                    <div className="space-y-1.5 pt-2 border-t border-slate-100">
                      <span className="font-bold text-slate-700 block">증빙 서류</span>
                      <div className="p-2.5 bg-slate-100 rounded-xl flex items-center justify-between">
                        <span className="truncate max-w-[200px] text-slate-600">증빙_첨부_파일</span>
                        <a
                          href={appr.ProofUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold"
                        >
                          문서 보기 ❯
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
                  <button
                    onClick={() => setSelectedLeaveDetail(null)}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Cancellation Reason Modal */}
        {cancelModalItem && (() => {
          const appr = cancelModalItem;
          const reqDays = Number(appr.LeaveDays || appr.Days || 0);

          return (
            <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-150 flex flex-col">
                <div className="bg-gradient-to-r from-rose-600 to-rose-800 px-6 py-4 flex items-center justify-between text-white">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">🔄</span>
                    <div>
                      <h3 className="font-extrabold text-sm text-white">연차 취소 신청</h3>
                      <p className="text-[10px] text-rose-200">결재권자의 승인 완료 시 연차가 자동 환원됩니다.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCancelModalItem(null)}
                    className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-6 space-y-4 text-xs text-left">
                  <div className="p-3.5 bg-rose-50 border border-rose-100 rounded-2xl space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-slate-500 font-bold">취소 대상 기간:</span>
                      <span className="font-extrabold text-slate-900">{appr.StartDate} {appr.StartDate !== appr.EndDate && `~ ${appr.EndDate}`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 font-bold">복구 예정 일수:</span>
                      <span className="font-extrabold text-emerald-700 bg-white px-2 py-0.5 rounded border border-emerald-200">+{reqDays}일 연차 환원</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-extrabold text-slate-700 block">
                      취소 사유 <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      value={cancelReasonInput}
                      onChange={e => setCancelReasonInput(e.target.value)}
                      placeholder="연차 취소 사유를 구체적으로 입력하세요 (예: 프로젝트 일정 변경으로 인한 휴가 취소)"
                      rows={3}
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 font-medium"
                    />
                  </div>
                </div>

                <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setCancelModalItem(null)}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    닫기
                  </button>
                  <button
                    onClick={() => {
                      if (!cancelReasonInput.trim()) {
                        alert('취소 사유를 입력해 주세요.');
                        return;
                      }
                      requestLeaveCancellation(appr.ApprovalID, cancelReasonInput.trim());
                      setCancelModalItem(null);
                      alert('연차 취소 결재가 상신되었습니다.\n결재권자의 승인이 완료되면 연차가 자동 복구됩니다.');
                    }}
                    className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-black transition-colors shadow-md cursor-pointer"
                  >
                    취소 결재 상신
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ----------------------------------------------------
  // RENDER VIEW B: EXPENSE MODE (Light Modern)
  // ----------------------------------------------------
  const pendingExpenseCount = expenseApprovals.filter(a => a.Status === 'PENDING').length;
  const approvedExpenseCount = expenseApprovals.filter(a => a.Status === 'APPROVED').length;
  const rejectedExpenseCount = expenseApprovals.filter(a => a.Status === 'REJECTED').length;
  const recentClaims = expenseApprovals.slice(0, 5);

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-slate-50 min-h-[calc(100vh-80px)] text-slate-800 space-y-6">
      
      {/* Page Head */}
      <div className="text-left pb-2">
        <h1 className="text-2xl font-bold text-slate-900">경비 처리</h1>
        <p className="text-xs text-slate-500 mt-1">법인카드 및 개인 경비 지출 내역을 등록하고 정산 결재를 신청합니다.</p>
      </div>

      {/* Metric Cards (Light Modern) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">이번 달 신청</span>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-3xl font-black text-slate-900">{thisMonthExpense.length}건</span>
          </div>
          <span className="text-[11px] text-slate-500 font-semibold block mt-2">
            ₩ {thisMonthExpenseAmount.toLocaleString()}
          </span>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">승인 완료</span>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-3xl font-black text-emerald-600">{approvedExpenseCount}건</span>
          </div>
          <span className="text-[11px] text-emerald-600 font-bold block mt-2">
            ₩ {approvedExpenseAmount.toLocaleString()}
          </span>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">승인 대기</span>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-3xl font-black text-amber-600">{pendingExpenseCount}건</span>
          </div>
          <span className="text-[11px] text-amber-600 font-bold block mt-2">
            ₩ {pendingExpenseAmount.toLocaleString()}
          </span>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs flex flex-col justify-between text-left">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">반려</span>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-3xl font-black text-rose-600">{rejectedExpenseCount}건</span>
          </div>
          <span className="text-[10px] text-rose-600 font-semibold block mt-2">보완 필요</span>
        </div>
      </div>

      {/* Form & Recent Processing Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: Expense Claim Registration */}
        <section ref={expenseFormRef} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <FileText className="w-5 h-5 text-indigo-600" />
            <h3 className="text-sm font-bold text-slate-900">경비 등록</h3>
          </div>

          <form onSubmit={handleExpenseSubmit} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center text-xs">
              <label className="sm:w-24 font-bold text-slate-600">사용일 *</label>
              <input className="flex-1 px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600" type="date" required defaultValue={todayStr} />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center text-xs">
              <label className="sm:w-24 font-bold text-slate-600">분류 *</label>
              <select 
                value={expenseCategory}
                onChange={e => setExpenseCategory(e.target.value)}
                className="flex-1 px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
              >
                <option value="교통비">교통비</option>
                <option value="식대">식대</option>
                <option value="소모품">소모품</option>
                <option value="회의비">회의비</option>
                <option value="기타">기타</option>
              </select>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center text-xs">
              <label className="sm:w-24 font-bold text-slate-600">결제 일자 *</label>
              <input
                className="flex-1 px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
                type="date"
                value={expenseDate}
                onChange={e => setExpenseDate(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center text-xs">
              <label className="sm:w-24 font-bold text-slate-600">금액 *</label>
              <input
                className="flex-1 px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                type="number"
                placeholder="0"
                value={expenseAmount}
                onChange={e => setExpenseAmount(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center text-xs">
              <label className="sm:w-24 font-bold text-slate-600">가맹점/사용처 *</label>
              <input
                className="flex-1 px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                type="text"
                placeholder="사용처를 입력하세요 (예: 스타벅스)"
                value={expenseDetails}
                onChange={e => setExpenseDetails(e.target.value)}
                required
              />
            </div>

            {/* Credit Card info columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-600 uppercase">결제 카드사</label>
                <select
                  value={cardCompany}
                  onChange={e => setCardCompany(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg py-2 px-3 text-slate-800 focus:outline-none"
                >
                  {CARD_COMPANIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {cardCompany === '직접 입력' && (
                  <input
                    type="text"
                    placeholder="카드사/은행명 직접 입력 (예: 신협, 카카오페이)"
                    value={customCardCompany}
                    onChange={e => setCustomCardCompany(e.target.value)}
                    className="mt-2 w-full bg-white border border-indigo-400 rounded-lg py-2 px-3 text-slate-900 focus:outline-none ring-1 ring-indigo-300 text-xs"
                    required
                  />
                )}
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">카드 번호 (선택)</label>
                <input
                  type="text"
                  placeholder="예: 1234 또는 xxxx-xxxx"
                  value={cardNumber}
                  onChange={e => setCardNumber(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg py-2 px-3 text-slate-800 focus:outline-none placeholder-slate-400"
                />
              </div>
            </div>

            {/* File & Camera Upload Receipt with OCR */}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-start text-xs">
              <label className="sm:w-24 font-bold text-slate-600 pt-2">영수증 *</label>
              <div className="flex-1 space-y-2 text-left">
                {/* 1. File Input (Gallery / Picker) */}
                <input
                  type="file"
                  ref={receiptFileInputRef}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleProcessReceiptImage(file);
                  }}
                  accept="image/*"
                  className="hidden"
                />

                {/* 2. Mobile Native Camera Capture Input */}
                <input
                  type="file"
                  ref={mobileCameraInputRef}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleProcessReceiptImage(file);
                  }}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                
                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => receiptFileInputRef.current?.click()}
                    className="bg-slate-50 border border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40 rounded-xl py-2.5 px-3 text-xs font-bold flex items-center justify-center gap-1.5 transition-all text-slate-700 cursor-pointer shadow-2xs"
                  >
                    <Upload className="w-4 h-4 text-indigo-600" />
                    <span>사진/파일 선택</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleStartCamera}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2.5 px-3 text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-sm"
                  >
                    <Camera className="w-4 h-4 text-white" />
                    <span>영수증 직접 촬영</span>
                  </button>
                </div>

                {/* OCR Scanning In-Progress Futuristic Hologram Indicator */}
                {isOcrScanning && (
                  <div className="p-4 bg-indigo-950/90 text-white border border-indigo-500/50 rounded-2xl space-y-3 shadow-lg relative overflow-hidden backdrop-blur-md animate-fade-in">
                    {/* Background Tech Grid Pattern */}
                    <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none" />
                    
                    <div className="flex justify-between items-center text-xs font-bold relative z-10">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-500/30 border border-indigo-400 rounded-lg text-indigo-300">
                          <Scan className="w-4 h-4 animate-spin text-cyan-400" />
                        </div>
                        <div>
                          <span className="text-cyan-300 font-extrabold tracking-wide block">✨ AI 영수증 광학 문자(OCR) 분석 중</span>
                          <span className="text-[11px] text-slate-300 font-medium">
                            {ocrProgress < 40 ? '📷 [1/3] 고화질 이미지 전처리 및 문자 검출' :
                             ocrProgress < 80 ? '🔍 [2/3] Tesseract AI 가맹점/금액 정밀 추출' :
                             '💳 [3/3] 12개 카드사 및 결제 상세 매칭 완료'}
                          </span>
                        </div>
                      </div>
                      <span className="font-mono text-base font-black text-cyan-300 px-2.5 py-1 bg-indigo-900/80 border border-cyan-500/40 rounded-xl">
                        {ocrProgress}%
                      </span>
                    </div>

                    {/* Progress Track */}
                    <div className="w-full bg-slate-800/80 border border-slate-700 rounded-full h-2 overflow-hidden relative z-10">
                      <div 
                        className="bg-gradient-to-r from-indigo-500 via-cyan-400 to-emerald-400 h-full rounded-full transition-all duration-300 shadow-[0_0_12px_rgba(6,182,212,0.8)]"
                        style={{ width: `${Math.max(ocrProgress, 15)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* OCR Result Notice */}
                {ocrNotice && (
                  <div className={`p-3 rounded-xl border flex items-center justify-between gap-2 text-xs font-semibold ${
                    ocrNotice.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
                    ocrNotice.type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-900' :
                    'bg-slate-100 border-slate-200 text-slate-800'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 flex-shrink-0 ${ocrNotice.type === 'success' ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <span>{ocrNotice.text}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOcrNotice(null)}
                      className="text-slate-400 hover:text-slate-700 cursor-pointer p-0.5"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Selected File & Image Preview with Laser Scan Effect */}
                {selectedFile && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-4 relative overflow-hidden">
                    <div className="flex items-center gap-2.5 overflow-hidden">
                      <FileCheck className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-bold text-slate-900 truncate block">{expenseFileName}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                      </div>
                    </div>
                    {previewUrl && (
                      <div className="relative w-16 h-16 rounded-xl border border-slate-200 overflow-hidden shadow-2xs flex-shrink-0 bg-slate-900">
                        <img 
                          src={previewUrl} 
                          alt="Receipt preview" 
                          className="w-full h-full object-cover" 
                        />
                        {/* Laser Scan Beam Overlay */}
                        {isOcrScanning && (
                          <>
                            <div className="absolute inset-0 bg-indigo-500/20 pointer-events-none" />
                            <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_8px_#22d3ee] animate-laser-scan pointer-events-none" />
                            <div className="absolute inset-0 border border-cyan-400/60 rounded-xl pointer-events-none" />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {uploadProgress > 0 && (
                  <div className="space-y-1.5 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-indigo-600">S3 직접 업로드 중...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* KakaoTalk Notification Toggle (Default ON) */}
            <div 
              id="kakao-expense-toggle-btn"
              onClick={() => setSendKakaoExpense(prev => !prev)}
              className="bg-amber-50/80 border border-amber-200/90 p-3 rounded-2xl flex items-center justify-between shadow-2xs cursor-pointer hover:bg-amber-100/60 transition-all select-none"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-amber-400 text-slate-950 flex items-center justify-center font-bold shadow-2xs flex-shrink-0">
                  <MessageSquare className="w-3.5 h-3.5 text-slate-900" />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 block">결재 담당자에게 카카오톡 승인 요청 알림 전송</span>
                  <span className="text-[11px] text-slate-600">신청 즉시 1차 결재자에게 카카오톡으로 경비 청구 및 영수증 확인 알림을 발송합니다.</span>
                </div>
              </div>
              <div className="relative inline-flex items-center flex-shrink-0 ml-3">
                <input
                  type="checkbox"
                  checked={sendKakaoExpense}
                  onChange={e => setSendKakaoExpense(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-9 h-5 rounded-full transition-all relative ${sendKakaoExpense ? 'bg-amber-500' : 'bg-slate-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-[2px] transition-all shadow-xs ${sendKakaoExpense ? 'right-[2px]' : 'left-[2px]'}`} />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button 
                type="button" 
                onClick={() => {
                  setExpenseAmount('');
                  setExpenseDetails('');
                  setExpenseFileName('');
                  setSelectedFile(null);
                  setPreviewUrl('');
                  setOcrNotice(null);
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer"
              >
                초기화
              </button>
              <button 
                type="submit" 
                disabled={isSubmittingExpense}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                <span>{isSubmittingExpense ? '신청 중...' : '경비 신청'}</span>
              </button>
            </div>
          </form>
        </section>

        {/* Live Camera Viewfinder Modal */}
        {isCameraModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl space-y-4 p-5 text-white text-center">
              <div className="flex justify-between items-center pb-2 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-indigo-400" />
                  <span className="font-bold text-sm">영수증 실시간 촬영</span>
                </div>
                <button
                  type="button"
                  onClick={handleStopCamera}
                  className="p-1.5 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Viewfinder Video Area */}
              <div className="relative bg-black rounded-2xl overflow-hidden aspect-4/3 flex items-center justify-center border border-slate-800">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                
                {/* Viewfinder Guide Overlay */}
                <div className="absolute inset-4 border-2 border-dashed border-white/50 rounded-xl pointer-events-none flex flex-col justify-between p-3">
                  <span className="text-[10px] bg-black/60 px-2 py-0.5 rounded text-white self-start">
                    영수증을 사각형 안에 맞추세요
                  </span>
                  <div className="w-full text-center">
                    <span className="text-[10px] text-white/80 bg-black/50 px-2.5 py-1 rounded-full">
                      가맹점과 금액이 선명하게 보이도록 촬영하세요
                    </span>
                  </div>
                </div>
              </div>

              {/* Shutter Button */}
              <div className="flex justify-center items-center gap-4 pt-2">
                <button
                  type="button"
                  onClick={handleStopCamera}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleCaptureWebcam}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full text-sm font-extrabold shadow-lg flex items-center gap-2 cursor-pointer transition-transform active:scale-95 ring-4 ring-indigo-500/30"
                >
                  <Camera className="w-5 h-5" />
                  <span>찰칵 (영수증 촬영 & OCR)</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Right column: Recent processed status */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs flex flex-col justify-between text-left">
          <div>
            <div className="pb-3 border-b border-slate-100 mb-4">
              <h3 className="text-sm font-bold text-slate-900">최근 처리 현황</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-700">
                <thead className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase">
                  <tr>
                    <th className="px-3 py-2.5">사용처</th>
                    <th className="px-3 py-2.5">금액</th>
                    <th className="px-3 py-2.5">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentClaims.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-6 text-slate-400">지출 청구 데이터 로그가 없습니다.</td>
                    </tr>
                  ) : (
                    recentClaims.map(c => {
                      const detailsStr = c.Details || '';
                      const cleanDetails = detailsStr.includes(']') 
                        ? detailsStr.substring(detailsStr.indexOf(']') + 1).trim()
                        : detailsStr;
                      
                      return (
                        <tr key={c.ApprovalID} className="hover:bg-slate-50">
                          <td className="px-3 py-2.5 font-semibold text-slate-900">{cleanDetails}</td>
                          <td className="px-3 py-2.5 font-mono text-slate-800">₩ {(Number(c.Amount) || 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full font-bold text-[9px] ${
                              c.Status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                              c.Status === 'REJECTED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                              'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {c.Status === 'APPROVED' ? '승인 완료' : c.Status === 'REJECTED' ? '반려' : '승인 대기'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-500 font-medium tracking-normal text-left leading-relaxed mt-4">
            모든 결재 영수증은 암호화 서명 발급 및 Presigned URL 기법을 이용해 클라우드 버킷 내 <strong>receipts/</strong> 폴더에 직접 업로드되며 실시간 감사 로그가 시스템 정책에 따라 저장됩니다.
          </div>
        </section>
      </div>

      {/* Bottom Panel: History request details */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs overflow-hidden text-left space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
              <span>경비 신청 내역</span>
              <span className="text-xs px-2.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-bold border border-indigo-200">
                총 {filteredExpenseApprovals.length}건
              </span>
            </h3>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              조회 기간 내 합계 금액: <strong className="text-indigo-600 font-mono font-bold">₩ {filteredExpenseTotalAmount.toLocaleString()}</strong>
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="사용처, 가맹점, 금액, 카드사 검색..."
              value={expenseSearchTerm}
              onChange={(e) => setExpenseSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600 focus:bg-white font-medium transition-all"
            />
            {expenseSearchTerm && (
              <button
                type="button"
                onClick={() => setExpenseSearchTerm('')}
                className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Period Filter Buttons (Requirement 6) */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: 'THIS_MONTH', label: '이번 달 (당월)' },
              { id: '3_MONTHS', label: '최근 3개월' },
              { id: '6_MONTHS', label: '최근 6개월' },
              { id: 'ALL', label: '전체 내역' },
              { id: 'CUSTOM', label: '직접 기간 선택' }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setExpensePeriodFilter(tab.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  expensePeriodFilter === tab.id
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range Picker */}
          {expensePeriodFilter === 'CUSTOM' && (
            <div className="flex items-center gap-2 animate-fade-in text-xs">
              <input
                type="date"
                value={expenseCustomStartDate}
                onChange={(e) => setExpenseCustomStartDate(e.target.value)}
                className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-600"
              />
              <span className="text-slate-400 font-bold">~</span>
              <input
                type="date"
                value={expenseCustomEndDate}
                onChange={(e) => setExpenseCustomEndDate(e.target.value)}
                className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-600"
              />
            </div>
          )}
        </div>

        {/* Mobile Card List for Expense */}
        <div className="block md:hidden space-y-3 pt-2">
          {filteredExpenseApprovals.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm bg-slate-50 rounded-2xl border border-slate-100">
              선택한 조건의 경비 신청 및 결재 내역이 없습니다.
            </div>
          ) : (
            filteredExpenseApprovals.slice((expensePage - 1) * ITEMS_PER_PAGE, expensePage * ITEMS_PER_PAGE).map(appr => (
              <div 
                key={appr.ApprovalID} 
                onClick={() => setSelectedExpenseDetail(appr)}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5 text-left shadow-2xs cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-start">
                  <span className="text-base font-extrabold text-indigo-700 font-mono">₩ {(Number(appr.Amount) || 0).toLocaleString()}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    appr.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                    appr.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {appr.Status === 'APPROVED' ? '승인 완료' : appr.Status === 'REJECTED' ? '반려됨' : '승인 대기'}
                  </span>
                </div>
                <div className="text-sm font-semibold text-slate-900">{appr.Details || '-'}</div>
                <div className="text-xs text-slate-500 flex items-center justify-between">
                  <span>{appr.CreatedAt ? new Date(appr.CreatedAt).toLocaleDateString() : '-'}</span>
                  {appr.CardCompany && (
                    <span className="font-bold text-slate-700">
                      💳 {appr.CardCompany} {appr.CardNumber ? `(${appr.CardNumber})` : ''}
                    </span>
                  )}
                </div>
                <div className="flex justify-between items-center pt-1 text-xs border-t border-slate-200/60">
                  <div className="flex items-center gap-2">
                    <span className="text-indigo-600 font-bold">결재 현황 및 상세 보기 ❯</span>
                    {appr.Status === 'PENDING' && (appr.ApplicantID === currentUser.UserID || appr.ApplicantName === currentUser.Name) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('대기 중인 경비 결재 신청을 취소(회수)하시겠습니까?')) {
                            withdrawExpenseRequest(appr.ApprovalID);
                          }
                        }}
                        className="px-2 py-0.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded text-[10px] font-bold transition-all cursor-pointer"
                      >
                        신청 취소
                      </button>
                    )}
                  </div>
                  {(appr.ReceiptS3Url || appr.ReceiptUrl || appr.ProofUrl) && <span className="text-slate-400">📎 영수증 첨부됨</span>}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table View for Expense */}
        <div className="hidden md:block overflow-x-auto pt-2">
          <table className="w-full text-left text-sm text-slate-700">
            <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 rounded-l-lg whitespace-nowrap">신청 번호</th>
                <th className="px-4 py-3 whitespace-nowrap">신청일</th>
                <th className="px-4 py-3 whitespace-nowrap">분류</th>
                <th className="px-4 py-3">사용처(내역)</th>
                <th className="px-4 py-3 whitespace-nowrap">금액</th>
                <th className="px-4 py-3 whitespace-nowrap">결제 정보</th>
                <th className="px-4 py-3 whitespace-nowrap">상세 / 영수증</th>
                <th className="px-4 py-3 rounded-r-lg whitespace-nowrap">상태</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredExpenseApprovals.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-10 text-slate-400">
                    선택한 조건의 경비 신청 및 결재 내역이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredExpenseApprovals.slice((expensePage - 1) * ITEMS_PER_PAGE, expensePage * ITEMS_PER_PAGE).map(appr => {
                  const detailsStr = appr.Details || '';
                  const hasCategory = detailsStr.startsWith('[') && detailsStr.includes(']');
                  const categoryText = hasCategory 
                    ? detailsStr.substring(1, detailsStr.indexOf(']'))
                    : '기타';
                  const cleanDetails = hasCategory 
                    ? detailsStr.substring(detailsStr.indexOf(']') + 1).trim()
                    : detailsStr;

                  return (
                    <tr 
                      key={appr.ApprovalID} 
                      onClick={() => setSelectedExpenseDetail(appr)}
                      className="hover:bg-indigo-50/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-mono text-slate-500 whitespace-nowrap">
                        {(appr.ApprovalID || '').substring(0, 14)}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{appr.CreatedAt ? new Date(appr.CreatedAt).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">
                        <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold">
                          {categoryText}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{cleanDetails}</td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-900 whitespace-nowrap">₩ {(Number(appr.Amount) || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {appr.CardCompany ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-800">
                            💳 {appr.CardCompany}
                            {appr.CardNumber ? <span className="font-mono text-slate-500 text-[11px]">({appr.CardNumber})</span> : ''}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedExpenseDetail(appr);
                            }}
                            className="px-2.5 py-1 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200 cursor-pointer"
                          >
                            상세보기 ❯
                          </button>
                          {appr.Status === 'PENDING' && (appr.ApplicantID === currentUser.UserID || appr.ApplicantName === currentUser.Name) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('대기 중인 경비 결재 신청을 취소(회수)하시겠습니까?')) {
                                  withdrawExpenseRequest(appr.ApprovalID);
                                }
                              }}
                              className="px-2 py-1 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors border border-rose-200 cursor-pointer"
                            >
                              취소
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold text-center ${
                          appr.Status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          appr.Status === 'REJECTED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                          'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {appr.Status === 'APPROVED' ? '승인 완료' : appr.Status === 'REJECTED' ? '반려됨' : '승인 대기'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination for User Expense History */}
        {filteredExpenseApprovals.length > ITEMS_PER_PAGE && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200 text-xs">
            <span className="text-slate-500 font-medium">
              전체 <strong className="text-slate-900 font-bold">{filteredExpenseApprovals.length.toLocaleString()}</strong>건 중{' '}
              <strong className="text-indigo-600 font-bold">{(expensePage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(expensePage * ITEMS_PER_PAGE, filteredExpenseApprovals.length)}</strong>건 표시 (페이지 {expensePage} / {Math.ceil(filteredExpenseApprovals.length / ITEMS_PER_PAGE)})
            </span>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setExpensePage(prev => Math.max(1, prev - 1))}
                disabled={expensePage === 1}
                className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                ‹ 이전
              </button>

              {Array.from({ length: Math.ceil(filteredExpenseApprovals.length / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setExpensePage(p)}
                  className={`w-7 h-7 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                    expensePage === p
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setExpensePage(prev => Math.min(Math.ceil(filteredExpenseApprovals.length / ITEMS_PER_PAGE), prev + 1))}
                disabled={expensePage === Math.ceil(filteredExpenseApprovals.length / ITEMS_PER_PAGE)}
                className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                다음 ›
              </button>
            </div>
          </div>
        )}

        {/* Payment Detail Modal with Visual Approval Pipeline Timeline (Requirement 5) */}
        {selectedExpenseDetail && (() => {
          const stages = (selectedExpenseDetail.ApprovalStages && selectedExpenseDetail.ApprovalStages.length > 0)
            ? selectedExpenseDetail.ApprovalStages
            : (typeof getAutoApprovalLine === 'function' ? getAutoApprovalLine({ UserID: selectedExpenseDetail.ApplicantID, Department: selectedExpenseDetail.ApplicantDept || '클라우드네이티브' }) : []);

          return (
            <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
                <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 px-6 py-4 flex justify-between items-center text-white">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💳</span>
                    <h3 className="font-bold text-base">경비 결제 상세 및 결재 현황</h3>
                  </div>
                  <button
                    onClick={() => setSelectedExpenseDetail(null)}
                    className="text-white/80 hover:text-white text-xl leading-none cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-6 space-y-4 text-left max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-xs font-bold text-slate-500">최종 처리 상태</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-extrabold ${
                      selectedExpenseDetail.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                      selectedExpenseDetail.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {selectedExpenseDetail.Status === 'APPROVED' ? '승인 완료' : selectedExpenseDetail.Status === 'REJECTED' ? '반려됨' : '결재 진행 중'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                      <div className="text-slate-400 font-semibold mb-1">결제 금액</div>
                      <div className="text-base font-extrabold text-indigo-700 font-mono">
                        ₩ {selectedExpenseDetail.Amount?.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                      <div className="text-slate-400 font-semibold mb-1">신청 일시</div>
                      <div className="font-bold text-slate-800">
                        {new Date(selectedExpenseDetail.CreatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                      <div className="text-slate-400 font-semibold mb-1">결제 카드사</div>
                      <div className="font-bold text-slate-800">
                        {selectedExpenseDetail.CardCompany || '카드사 정보 없음'}
                      </div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                      <div className="text-slate-400 font-semibold mb-1">카드 번호</div>
                      <div className="font-mono font-bold text-slate-800">
                        {selectedExpenseDetail.CardNumber || '-'}
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 text-xs">
                    <div className="text-slate-400 font-semibold mb-1">사용처 / 사용 상세 내역</div>
                    <div className="font-bold text-slate-900 text-sm">{selectedExpenseDetail.Details}</div>
                  </div>

                  {/* Visual Approval Timeline */}
                  <div className="space-y-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-800">결재 진행 현황 및 히스토리</span>
                      <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                        총 {stages.length}단계 결재 라인
                      </span>
                    </div>

                    <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                      {/* Step 0: Applicant submission */}
                      <div className="relative flex items-start gap-3">
                        <div className="absolute -left-6 top-0.5 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold shadow-xs">
                          ✓
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-extrabold text-slate-900">
                              {selectedExpenseDetail.ApplicantName || currentUser.Name}
                            </span>
                            <span className="text-[11px] text-slate-500">신청 제출</span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {new Date(selectedExpenseDetail.CreatedAt).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Approval Stages */}
                      {stages.map((stage, sIdx) => {
                        const isDone = stage.Status === 'APPROVED' || selectedExpenseDetail.Status === 'APPROVED';
                        const isRejected = stage.Status === 'REJECTED' || selectedExpenseDetail.Status === 'REJECTED';
                        const isCurrent = !isDone && !isRejected && (selectedExpenseDetail.CurrentStep === stage.Step || sIdx === 0);

                        return (
                          <div key={sIdx} className="relative flex items-start gap-3">
                            <div className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow-xs ${
                              isDone ? 'bg-emerald-500 text-white' :
                              isRejected ? 'bg-rose-500 text-white' :
                              isCurrent ? 'bg-indigo-600 text-white animate-pulse ring-2 ring-indigo-300' :
                              'bg-slate-200 text-slate-500'
                            }`}>
                              {isDone ? '✓' : isRejected ? '✕' : stage.Step || sIdx + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-bold text-slate-900">
                                    {stage.ApproverName || `결재자 ${stage.Step || sIdx + 1}단계`}
                                  </span>
                                  <span className="text-[10px] px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded font-semibold">
                                    {stage.ApproverJobTitle || '결재자'}
                                  </span>
                                </div>
                                <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                                  isDone ? 'bg-emerald-100 text-emerald-800' :
                                  isRejected ? 'bg-rose-100 text-rose-800' :
                                  isCurrent ? 'bg-amber-100 text-amber-800' :
                                  'bg-slate-100 text-slate-400'
                                }`}>
                                  {isDone ? '승인 완료' : isRejected ? '반려됨' : isCurrent ? '결재 대기 중' : '대기'}
                                </span>
                              </div>
                              {stage.ActionAt && (
                                <span className="text-[10px] text-slate-400 font-mono block mt-0.5">
                                  처리일시: {new Date(stage.ActionAt).toLocaleString()}
                                </span>
                              )}
                              {stage.Comment && (
                                <p className="text-[11px] text-slate-600 bg-slate-50 p-1.5 rounded-lg mt-1 border border-slate-100">
                                  "{stage.Comment}"
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {(selectedExpenseDetail.ReceiptS3Url || selectedExpenseDetail.ReceiptUrl || selectedExpenseDetail.ProofUrl) && (
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      <div className="text-xs font-bold text-slate-700">영수증 증빙 파일</div>
                      <div className="p-3 bg-slate-100 rounded-xl flex items-center justify-between">
                        <span className="text-xs text-slate-700 truncate max-w-[200px]">
                          {selectedExpenseDetail.ProofFileName || selectedExpenseDetail.FileName || '영수증_첨부.jpg'}
                        </span>
                        <a
                          href={selectedExpenseDetail.ReceiptS3Url || selectedExpenseDetail.ReceiptUrl || selectedExpenseDetail.ProofUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        >
                          영수증 원본 열기 ❯
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  {selectedExpenseDetail.Status === 'PENDING' && (selectedExpenseDetail.ApplicantID === currentUser.UserID || selectedExpenseDetail.ApplicantName === currentUser.Name) ? (
                    <button
                      onClick={() => {
                        if (window.confirm('대기 중인 경비 결재 신청을 즉시 취소(회수)하시겠습니까?')) {
                          withdrawExpenseRequest(selectedExpenseDetail.ApprovalID);
                          setSelectedExpenseDetail(null);
                        }
                      }}
                      className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                    >
                      🗑️ 신청 취소 (결재 회수)
                    </button>
                  ) : <div />}
                  <button
                    onClick={() => setSelectedExpenseDetail(null)}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </section>
    </div>
  );
};

export default LeaveExpenseTab;
